import React, { useState, useCallback } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, ActivityIndicator, RefreshControl, Alert, Platform } from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect, CompositeNavigationProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useTheme } from '../../theme/ThemeContext';
import { ClubsStackParamList, RootStackParamList } from '../../types/navigation';
import ThemedText from '../../components/common/ThemedText';
import { getMyReferralRequests, getReferralRequests, acceptIntro, rejectIntro, closeReferralRequest, ReferralRequest } from '../../lib/api/referrals';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { getTimeAgo } from '../../utils/timeAgo';
import FeedVideoPlayer from '../../components/feed/FeedVideoPlayer';

type ReferralRequestDetailNavigationProp = CompositeNavigationProp<
  StackNavigationProp<ClubsStackParamList, 'ReferralRequestDetail'>,
  StackNavigationProp<RootStackParamList>
>;
type ReferralRequestDetailRouteProp = RouteProp<ClubsStackParamList, 'ReferralRequestDetail'>;

const ReferralRequestDetailScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<ReferralRequestDetailNavigationProp>();
  const route = useRoute<ReferralRequestDetailRouteProp>();
  const { requestId } = route.params;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [request, setRequest] = useState<ReferralRequest | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      loadRequest();
    }, [requestId])
  );

  const loadRequest = async () => {
    try {
      setLoading(true);
      const clubId = route.params.clubId || '';
      
      // Try to find in my requests first (if I'm the owner)
      let myRequests = await getMyReferralRequests(clubId);
      let foundRequest = myRequests.find(r => r.id === requestId);
      
      // If not found in my requests, fetch from all club requests (if someone sent me an intro)
      if (!foundRequest) {
        const allRequests = await getReferralRequests(clubId);
        foundRequest = allRequests.find(r => r.id === requestId);
      }
      
      setRequest(foundRequest || null);
    } catch (error) {
      console.error('Error loading request:', error);
      showErrorToast('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadRequest();
    setRefreshing(false);
  };

  const handleAccept = async (responseId: string) => {
    try {
      setProcessingId(responseId);
      await acceptIntro(responseId);
      showSuccessToast('Intro accepted! Responder earned 10 points');
      await loadRequest();
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to accept intro');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (responseId: string) => {
    const confirmReject = () => {
      if (Platform.OS === 'web') {
        return window.confirm('Are you sure you want to pass on this introduction?');
      }
      return new Promise<boolean>((resolve) => {
        Alert.alert(
          'Pass Introduction',
          'Are you sure you want to pass on this introduction?',
          [
            { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
            {
              text: 'Pass',
              style: 'destructive',
              onPress: () => resolve(true),
            },
          ]
        );
      });
    };

    const confirmed = await confirmReject();
    if (!confirmed) return;

    try {
      setProcessingId(responseId);
      await rejectIntro(responseId);
      showSuccessToast('Intro rejected');
      await loadRequest();
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to reject intro');
    } finally {
      setProcessingId(null);
    }
  };

  const handleClose = async () => {
    const confirmClose = () => {
      if (Platform.OS === 'web') {
        return window.confirm('Are you sure you want to close this referral request? It will no longer accept new introductions.');
      }
      return new Promise<boolean>((resolve) => {
        Alert.alert(
          'Close Request',
          'Are you sure you want to close this referral request? It will no longer accept new introductions.',
          [
            { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
            {
              text: 'Close',
              style: 'destructive',
              onPress: () => resolve(true),
            },
          ]
        );
      });
    };

    const confirmed = await confirmClose();
    if (!confirmed) return;

    try {
      await closeReferralRequest(requestId);
      showSuccessToast('Request closed');
      navigation.goBack();
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to close request');
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.accent} />
        </View>
      </SafeAreaView>
    );
  }

  if (!request) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={48} color={theme.textTertiary} />
          <ThemedText style={[styles.errorText, { color: theme.textTertiary }]}>
            Request not found
          </ThemedText>
        </View>
      </SafeAreaView>
    );
  }

  const responses = request.responses || [];
  const pendingResponses = responses.filter(r => r.status === 'pending');
  const acceptedResponses = responses.filter(r => r.status === 'accepted');
  const rejectedResponses = responses.filter(r => r.status === 'rejected');

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: theme.card, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.headerButton} activeOpacity={0.7}>
          <Ionicons name="arrow-back" size={28} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>Request Details</ThemedText>
        <TouchableOpacity onPress={handleClose} style={styles.headerButton} activeOpacity={0.7}>
          <Ionicons name="close-circle-outline" size={24} color={theme.error} />
        </TouchableOpacity>
      </View>

      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={theme.accent} />
        }
      >
        {/* Request Card */}
        <View style={[styles.requestCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
          {request.videoUrl ? (
            <>
              <View style={styles.section}>
                <ThemedText style={[styles.sectionLabel, { color: theme.textSecondary }]}>
                  Video Request
                </ThemedText>
                <FeedVideoPlayer uri={request.videoUrl} style={styles.requestVideo} />
              </View>
            </>
          ) : (
            <>
              {request.businessName ? (
                <ThemedText style={[styles.businessName, { color: theme.text }]}>
                  {request.businessName}
                </ThemedText>
              ) : null}
              {request.businessDetails ? (
                <View style={styles.section}>
                  <ThemedText style={[styles.sectionLabel, { color: theme.textSecondary }]}>
                    About
                  </ThemedText>
                  <ThemedText style={[styles.sectionText, { color: theme.text }]}>
                    {request.businessDetails}
                  </ThemedText>
                </View>
              ) : null}
              {request.lookingFor ? (
                <View style={styles.section}>
                  <ThemedText style={[styles.sectionLabel, { color: theme.textSecondary }]}>
                    Looking For
                  </ThemedText>
                  <ThemedText style={[styles.sectionText, { color: theme.text }]}>
                    {request.lookingFor}
                  </ThemedText>
                </View>
              ) : null}
            </>
          )}
          <View style={[styles.statsRow, { borderTopColor: theme.border }]}>
            <View style={styles.stat}>
              <ThemedText style={[styles.statValue, { color: theme.accent }]}>
                {responses.length}
              </ThemedText>
              <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
                Total Intros
              </ThemedText>
            </View>
            <View style={styles.stat}>
              <ThemedText style={[styles.statValue, { color: '#10B981' }]}>
                {acceptedResponses.length}
              </ThemedText>
              <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
                Accepted
              </ThemedText>
            </View>
            <View style={styles.stat}>
              <ThemedText style={[styles.statValue, { color: '#F59E0B' }]}>
                {pendingResponses.length}
              </ThemedText>
              <ThemedText style={[styles.statLabel, { color: theme.textSecondary }]}>
                Pending
              </ThemedText>
            </View>
          </View>
        </View>

        {/* Responses */}
        {responses.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Ionicons name="mail-open-outline" size={48} color={theme.textTertiary} />
            <ThemedText style={[styles.emptyText, { color: theme.textSecondary }]}>
              No introductions yet
            </ThemedText>
            <ThemedText style={[styles.emptySubtext, { color: theme.textTertiary }]}>
              Members will submit introductions here
            </ThemedText>
          </View>
        ) : (
          <>
            {pendingResponses.length > 0 && (
              <View style={styles.responseSection}>
                <ThemedText style={[styles.responseSectionTitle, { color: theme.text }]}>
                  Pending ({pendingResponses.length})
                </ThemedText>
                {pendingResponses.map(response => renderResponse(response))}
              </View>
            )}

            {acceptedResponses.length > 0 && (
              <View style={styles.responseSection}>
                <ThemedText style={[styles.responseSectionTitle, { color: theme.text }]}>
                  Accepted ({acceptedResponses.length})
                </ThemedText>
                {acceptedResponses.map(response => renderResponse(response))}
              </View>
            )}

            {rejectedResponses.length > 0 && (
              <View style={styles.responseSection}>
                <ThemedText style={[styles.responseSectionTitle, { color: theme.text }]}>
                  Rejected ({rejectedResponses.length})
                </ThemedText>
                {rejectedResponses.map(response => renderResponse(response))}
              </View>
            )}
          </>
        )}
      </ScrollView>
    </SafeAreaView>
  );

  function renderResponse(response: any) {
    const isPending = response.status === 'pending';
    const isAccepted = response.status === 'accepted';
    const isProcessing = processingId === response.id;

    const handleViewProfile = () => {
      navigation.navigate('Profile', { userId: response.responder.id });
    };

    return (
      <View key={response.id} style={[styles.responseCard, { backgroundColor: theme.card, borderColor: theme.border }]}>
        {/* Responder Info */}
        <TouchableOpacity 
          style={styles.responderInfo}
          onPress={handleViewProfile}
          activeOpacity={0.7}
        >
          {response.responder.photo ? (
            <Image
              source={{ uri: response.responder.photo }}
              style={styles.avatar}
              contentFit="cover"
            />
          ) : (
            <View style={[styles.avatarPlaceholder, { backgroundColor: theme.accent + '20' }]}>
              <Ionicons name="person" size={20} color={theme.accent} />
            </View>
          )}
          <View style={styles.responderDetails}>
            <View style={styles.nameRow}>
              <ThemedText style={[styles.responderName, { color: theme.text }]}>
                {response.responder.name || 'Anonymous'}
              </ThemedText>
              <Ionicons name="chevron-forward" size={16} color={theme.textTertiary} />
            </View>
            <ThemedText style={[styles.timestamp, { color: theme.textTertiary }]}>
              {getTimeAgo(response.createdAt)}
            </ThemedText>
          </View>
        </TouchableOpacity>

        {/* Contact Details */}
        <View style={styles.contactSection}>
          {response.contactName && response.contactName !== 'Video Introduction' ? (
            <View style={styles.contactRow}>
              <Ionicons name="person-outline" size={16} color={theme.textSecondary} />
              <ThemedText style={[styles.contactText, { color: theme.text }]}>
                {response.contactName}
              </ThemedText>
            </View>
          ) : null}
          {response.contactPhone && response.contactPhone !== 'N/A' ? (
            <View style={styles.contactRow}>
              <Ionicons name="call-outline" size={16} color={theme.textSecondary} />
              <ThemedText style={[styles.contactText, { color: theme.text }]}>
                {response.contactPhone}
              </ThemedText>
            </View>
          ) : null}
          {response.videoUrl ? (
            <FeedVideoPlayer
              uri={response.videoUrl}
              style={styles.responseVideo}
            />
          ) : null}
          {response.contactDetails && response.contactDetails !== 'Video Introduction' ? (
            <View style={[styles.detailsBox, { backgroundColor: theme.background }]}>
              <ThemedText style={[styles.contactDetails, { color: theme.text }]}>
                {response.contactDetails}
              </ThemedText>
            </View>
          ) : null}
        </View>

        {/* Actions */}
        {isPending && (
          <View style={styles.actionRow}>
            <TouchableOpacity
              style={[styles.rejectButton, { backgroundColor: theme.error + '20' }]}
              onPress={() => handleReject(response.id)}
              disabled={isProcessing}
              activeOpacity={0.7}
            >
              {isProcessing ? (
                <ActivityIndicator size="small" color={theme.error} />
              ) : (
                <>
                  <Ionicons name="close-circle" size={20} color={theme.error} />
                  <ThemedText style={[styles.buttonText, { color: theme.error }]}>
                    Pass
                  </ThemedText>
                </>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.acceptButton, { backgroundColor: '#10B981' }]}
              onPress={() => handleAccept(response.id)}
              disabled={isProcessing}
              activeOpacity={0.7}
            >
              {isProcessing ? (
                <ActivityIndicator size="small" color="#FFF" />
              ) : (
                <>
                  <Ionicons name="checkmark-circle" size={20} color="#FFF" />
                  <ThemedText style={[styles.buttonText, { color: '#FFF' }]}>
                    Accept
                  </ThemedText>
                </>
              )}
            </TouchableOpacity>
          </View>
        )}

        {!isPending && (
          <View style={[
            styles.statusBadge,
            {
              backgroundColor: isAccepted ? '#10B981' + '20' : theme.error + '20',
            }
          ]}>
            <Ionicons
              name={isAccepted ? 'checkmark-circle' : 'close-circle'}
              size={16}
              color={isAccepted ? '#10B981' : theme.error}
            />
            <ThemedText style={[
              styles.statusText,
              { color: isAccepted ? '#10B981' : theme.error }
            ]}>
              {response.status.charAt(0).toUpperCase() + response.status.slice(1)}
            </ThemedText>
          </View>
        )}
      </View>
    );
  }
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  errorText: {
    fontSize: 16,
    marginTop: 16,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  requestCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    borderWidth: 1,
  },
  businessName: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 16,
  },
  section: {
    marginBottom: 16,
  },
  requestVideo: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 14,
    marginTop: 8,
    overflow: 'hidden',
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  sectionText: {
    fontSize: 14,
    lineHeight: 20,
  },
  statsRow: {
    flexDirection: 'row',
    borderTopWidth: 1,
    paddingTop: 16,
    marginTop: 8,
  },
  stat: {
    flex: 1,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
  },
  statLabel: {
    fontSize: 12,
    marginTop: 4,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    marginTop: 8,
  },
  responseSection: {
    marginBottom: 24,
  },
  responseSectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 12,
  },
  responseCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
  },
  responderInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  avatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  responderDetails: {
    marginLeft: 12,
    flex: 1,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  responderName: {
    fontSize: 15,
    fontWeight: '600',
  },
  timestamp: {
    fontSize: 12,
    marginTop: 2,
  },
  contactSection: {
    marginBottom: 16,
  },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  contactText: {
    fontSize: 14,
    fontWeight: '500',
  },
  detailsBox: {
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
  },
  responseVideo: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 14,
    marginTop: 10,
    marginBottom: 8,
    overflow: 'hidden',
  },
  contactDetails: {
    fontSize: 14,
    lineHeight: 20,
  },
  actionRow: {
    flexDirection: 'row',
    gap: 12,
  },
  rejectButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    gap: 6,
  },
  acceptButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    gap: 6,
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    borderRadius: 8,
    gap: 6,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default ReferralRequestDetailScreen;
